import {
  evidenceSearchSchema,
  evidenceSourceSchema,
  type EvidenceSearch,
  type EvidenceSource,
} from '@cadencia/contracts';
import { canPromote, retractSource } from '@cadencia/domain';
import { logger } from '../../lib/logger.js';
import { todayLocal } from '../../lib/dates.js';
import { db } from '../db.js';
import { parchear } from '../parchear.js';
import { newSyncColumns, putRow, type WriteContext } from '../writes.js';

/**
 * La base de evidencia: fichas y búsquedas (§8, migración 0031).
 *
 * Como el resto de repositorios: al leer `safeParse` y se registra lo inválido
 * (ADR-035), al escribir `parse`.
 *
 * Aquí no hay ninguna función que siembre nada. La base nace vacía y se llena
 * solo con lo que una búsqueda real traiga: una ficha de ejemplo sería una
 * referencia inventada, que es lo que el §8 prohíbe en su última línea.
 */

function normalizar<T>(
  filas: readonly unknown[],
  esquema: { safeParse: (value: unknown) => { success: boolean; data?: unknown } },
  tabla: string,
): T[] {
  const salida: T[] = [];
  let invalidas = 0;
  for (const fila of filas) {
    const resultado = esquema.safeParse(fila);
    salida.push((resultado.success ? resultado.data : fila) as T);
    if (!resultado.success) invalidas += 1;
  }
  if (invalidas > 0) {
    logger.warn('db', 'EVIDENCE_SHAPE_MISMATCH', 'Filas locales que no encajan con su contrato', {
      tabla,
      invalidas,
    });
  }
  return salida;
}

// ---------------------------------------------------------------------------
// Lectura
// ---------------------------------------------------------------------------

export async function listSources(userId: string): Promise<EvidenceSource[]> {
  const filas = (await db.evidenceSources.where('userId').equals(userId).toArray()).filter(
    (fila) => fila.deletedAt === null,
  );
  return normalizar<EvidenceSource>(filas, evidenceSourceSchema, 'evidenceSources').sort((a, b) =>
    b.reviewedOn.localeCompare(a.reviewedOn),
  );
}

export async function listSearches(userId: string): Promise<EvidenceSearch[]> {
  const filas = (await db.evidenceSearches.where('userId').equals(userId).toArray()).filter(
    (fila) => fila.deletedAt === null,
  );
  return normalizar<EvidenceSearch>(filas, evidenceSearchSchema, 'evidenceSearches').sort((a, b) =>
    b.searchedOn.localeCompare(a.searchedOn),
  );
}

// ---------------------------------------------------------------------------
// Escritura
// ---------------------------------------------------------------------------

export type EvidenceSearchInput = Omit<
  EvidenceSearch,
  keyof ReturnType<typeof newSyncColumns> | 'userId'
>;

export async function saveSearch(
  ctx: WriteContext,
  input: EvidenceSearchInput,
): Promise<EvidenceSearch> {
  const row = evidenceSearchSchema.parse({ ...newSyncColumns(ctx), ...input });
  return putRow(ctx, 'evidenceSearches', row);
}

export type EvidenceSourceInput = Omit<
  EvidenceSource,
  keyof ReturnType<typeof newSyncColumns> | 'userId'
>;

/**
 * Guarda una ficha nueva.
 *
 * SIEMPRE `candidate`, y no es un valor por defecto que se pueda pasar por
 * encima: quien llama no puede pedir que nazca activa. El §8 separa «búsqueda y
 * síntesis» de «evaluación y activación», y esa frontera solo existe de verdad
 * si el camino que trae fichas no puede saltársela. Activar es `promote`, que
 * es otra función y la llama una persona desde una pantalla.
 */
export async function saveCandidate(
  ctx: WriteContext,
  input: Omit<EvidenceSourceInput, 'status' | 'version'>,
): Promise<EvidenceSource> {
  const row = evidenceSourceSchema.parse({
    ...newSyncColumns(ctx),
    ...input,
    status: 'candidate',
    version: 1,
  });
  return putRow(ctx, 'evidenceSources', row);
}

export interface PromotionResult {
  source: EvidenceSource | null;
  /** Por qué no se pudo, en vocabulario del dominio. Vacío si se promovió. */
  blockers: readonly string[];
}

/**
 * Activa una ficha candidata, si el dominio lo permite.
 *
 * `nota` es obligatoria y no es burocracia: el §8 pide «registrar cómo se
 * verificó», y sin ella el historial diría que alguien pulsó un botón. Con
 * ella dice qué miró.
 */
export async function promote(
  ctx: WriteContext,
  source: EvidenceSource,
  nota: string,
): Promise<PromotionResult> {
  const veredicto = canPromote(source);
  if (!veredicto.promote) return { source: null, blockers: veredicto.blockers };

  const row = parchear(
    evidenceSourceSchema,
    source,
    {
      status: 'active',
      statusNote: nota,
      reviewedOn: todayLocal(),
      version: Math.min(999, source.version + 1),
    },
    { tabla: 'evidence_sources', id: source.id },
  );
  return { source: await putRow(ctx, 'evidenceSources', row), blockers: [] };
}

/**
 * Retira una fuente sin borrarla (§8).
 *
 * La regla vive en el dominio (`retractSource`) y aquí solo se persiste: un
 * bloque de hace tres meses citó lo que citó, y lo que no puede pasar es que
 * el pasado se reescriba para que parezca que nunca se apoyó en algo que
 * después se cayó.
 */
export async function retract(
  ctx: WriteContext,
  source: EvidenceSource,
  motivo: string,
): Promise<EvidenceSource> {
  const actualizada = retractSource(source, motivo, todayLocal());
  const row = parchear(
    evidenceSourceSchema,
    source,
    {
      status: actualizada.status,
      statusNote: actualizada.statusNote,
      reviewedOn: actualizada.reviewedOn,
      version: actualizada.version,
    },
    { tabla: 'evidence_sources', id: source.id },
  );
  return putRow(ctx, 'evidenceSources', row);
}
